import type {
  FieldType,
  RangedFieldTypes,
  ResultRecord,
} from '@dynatrace-sdk/client-query';
import {
  units,
  type ConvertibleUnit,
  type FormatOptions,
  type Unit,
} from '@dynatrace-sdk/units';

import type { DataTableColumnDef } from '../DataTable/public.api.js';

function extractColumnTypes(
  rangedFieldTypes: RangedFieldTypes[],
): Map<string, Set<FieldType['type']>> {
  const columnTypes = new Map<string, Set<FieldType['type']>>();
  for (const { mappings } of rangedFieldTypes) {
    for (const key in mappings) {
      const type = mappings[key]?.type ?? 'string';
      if (columnTypes.has(key)) {
        columnTypes.get(key)?.add(type);
      } else {
        columnTypes.set(key, new Set([type]));
      }
    }
  }
  return columnTypes;
}

/**
 * Mapping from a Grail type to a built-in column type of the DataTable
 */
const FieldTypeTypeToColumnType = new Map<
  FieldType['type'],
  'datetime' | 'number' | 'long' | undefined
>([
  ['timestamp', 'datetime'],
  ['long', 'long'],
  ['double', 'number'],
  ['duration', 'number'],
]);

/**
 * Mapping from a Grail type to a built-in predefined formatter.
 */
const FieldTypeTypeToFormatter = new Map<
  FieldType['type'],
  FormatOptions<Unit, ConvertibleUnit>
>([
  [
    'duration',
    {
      input: units.time.nanosecond,
      cascade: 3,
    },
  ],
]);

function isTimeFrameEntry(
  value: unknown,
): value is { start: Date | string; end: Date | string } {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const { start, end } = value as { start: unknown; end: unknown };

  return (
    (typeof start === 'string' || start instanceof Date) &&
    (typeof end === 'string' || end instanceof Date)
  );
}

function isTimeGeoPointEntry(
  value: unknown,
): value is { latitude: number; longitude: number } {
  if (value === null || typeof value !== 'object') {
    return false;
  }

  const { latitude, longitude } = value as {
    latitude: unknown;
    longitude: unknown;
  };

  return typeof latitude === 'number' && typeof longitude === 'number';
}

function getTimeframeColumn(
  columnKey: string,
): DataTableColumnDef<ResultRecord | null> {
  // For timeframes, we can split it into two columns for start and end.
  return {
    header: columnKey,
    id: columnKey,
    columns: [
      {
        header: 'start',
        id: `${columnKey}.start`,
        accessor: (row) => {
          const entry = row?.[columnKey];
          if (isTimeFrameEntry(entry)) {
            const rawDate = entry.start;
            if (rawDate instanceof Date) {
              return rawDate;
            }
            const date = new Date(rawDate);
            return Number.isNaN(date.getTime()) ? '' : date;
          }
          return '';
        },
        columnType: 'datetime',
        width: 'content',
      },
      {
        header: 'end',
        id: `${columnKey}.end`,
        accessor: (row) => {
          const entry = row?.[columnKey];
          if (isTimeFrameEntry(entry)) {
            const rawDate = entry.end;
            if (rawDate instanceof Date) {
              return rawDate;
            }
            const date = new Date(rawDate);
            return Number.isNaN(date.getTime()) ? '' : date;
          }
          return '';
        },
        columnType: 'datetime',
        width: 'content',
      },
    ],
  };
}

function getGeoPointColumn(
  columnKey: string,
): DataTableColumnDef<ResultRecord | null> {
  // For geopoints, we can split it into two columns for longitude and latitude.
  return {
    header: columnKey,
    id: columnKey,
    columns: [
      {
        header: 'longitude',
        id: `${columnKey}.longitude`,
        accessor: (row) => {
          if (isTimeGeoPointEntry(row?.[columnKey])) {
            return row[columnKey].longitude;
          }
          return '';
        },
        columnType: 'text',
        width: 'content',
      },
      {
        header: 'latitude',
        id: `${columnKey}.latitude`,
        accessor: (row) => {
          if (isTimeGeoPointEntry(row?.[columnKey])) {
            return row[columnKey].latitude;
          }
          return '';
        },
        columnType: 'text',
        width: 'content',
      },
    ],
  };
}

/**
 * Converts Grail query result type mappings into DataTable column definitions.
 *
 * @param rangedFieldTypes - The type mappings from a Grail query result.
 * @returns An array of DataTable column definitions.
 *
 * @public
 */
export function convertToColumns(
  rangedFieldTypes: RangedFieldTypes[],
): DataTableColumnDef<ResultRecord | null>[] {
  const extractedTypes = extractColumnTypes(rangedFieldTypes);
  const columns: DataTableColumnDef<ResultRecord | null>[] = [];

  for (const [columnKey, columnTypes] of extractedTypes.entries()) {
    const columnHasOnlyOneType = columnTypes.size === 1;
    const columnType = columnHasOnlyOneType
      ? Array.from(columnTypes)[0]
      : 'string'; // ambiguous definition, fall back to text

    if (columnType === 'timeframe') {
      columns.push(getTimeframeColumn(columnKey));
    } else if (columnType === 'geo_point') {
      columns.push(getGeoPointColumn(columnKey));
    } else {
      columns.push({
        header: columnKey,
        id: columnKey,
        accessor: (row) => row?.[columnKey],
        width: 'content',
        columnType: FieldTypeTypeToColumnType.get(columnType) ?? 'text',
        formatter: FieldTypeTypeToFormatter.get(columnType) ?? undefined,
      } as DataTableColumnDef<ResultRecord | null>);
    }
  }
  return columns;
}
